import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter } from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import { routes } from "./app.route";
import { provideHttpClient, withInterceptors, withFetch, HttpClient } from "@angular/common/http";
import { authInterceptor } from "./core/interceptor/auth.interceptor";
import { LoadingInterceptor } from "./core/interceptor/loading.interceptor";
import { maintenanceInterceptor } from "./core/interceptor/maintenance.interceptor";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { SUBDOMAIN_TOKEN } from "./core/token/subdomain.token";
import { readSubdomainFromHostname } from "./core/service/tenant.service";
import { Title } from "@angular/platform-browser";
import { provideToastr } from "ngx-toastr";

// service
import { AppService } from "./core/service/app.service";

// Store
import { StoreModule } from "@ngrx/store";
import { indexReducer } from "./store/index.reducer";

// lottie
import { provideLottieOptions } from "ngx-lottie";
import player from "lottie-web";

// perfect-scrollbar
import { NgScrollbarModule } from "ngx-scrollbar";

// ngx-echarts
import { provideEchartsCore } from "ngx-echarts";
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { CustomTranslateHttpLoader } from "./core/service/custom-translate-http-loader.service";


// Register required charts/components/renderers for ECharts
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  CanvasRenderer,
]);

// Factory function to extract subdomain from URL (empty when the host has no tenant).
export function getSubdomain(): string {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return readSubdomainFromHostname(window.location.hostname);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateHttpLoader(http);
}

// AOT compilation support - HTTP loader factory for translations
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    "../i18n/components-translation/user/",
    ".json"
  );
}

export const provideTranslation = () => ({
  defaultLanguage: "en",
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});


  

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(routes),
    provideToastr({
      timeOut: 3000,
      positionClass: "toast-top-right",
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
    }),
    Title,
    AppService,
    {
      provide: SUBDOMAIN_TOKEN,
      useFactory: getSubdomain,
    },
    importProvidersFrom([
      TranslateModule.forRoot(provideTranslation()),
      StoreModule.forRoot({ index: indexReducer }),
      NgScrollbarModule,
    ]),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, LoadingInterceptor, maintenanceInterceptor])),
    provideAnimations(),
    provideEchartsCore({ echarts: () => import("echarts/core") }),
    // Provide Lottie player factory
    provideLottieOptions({
      player: () => player,
    }),
  ],
};
